import React from "react";
import { motion } from "framer-motion";
import { Download, GraduationCap, Award, FileText, ArrowUpRight } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { resumeHighlights, personalInfo } from "../data/mock";

const ResumeSection = () => {
  const { education, certifications, quickStats } = resumeHighlights;

  return (
    <section id="resume" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow="Resume"
          title="Quick highlights."
          description="The fast version. Grab the PDF for the full story."
        />

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-10">
          {quickStats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="p-5 rounded-2xl glass"
            >
              <p className="font-display text-3xl md:text-4xl font-semibold bg-gradient-to-br from-cyan-200 to-cyan-400 bg-clip-text text-transparent">
                {s.value}
              </p>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-1">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Education */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="p-6 rounded-2xl glass"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-cyan-300" />
              </div>
              <h3 className="font-display text-lg font-semibold text-white">Education</h3>
            </div>
            <p className="font-display text-base text-white">{education.institution}</p>
            <p className="text-sm text-zinc-400 mt-1">{education.degree}</p>
            <div className="mt-3 flex items-center gap-3 text-xs">
              <span className="px-2.5 py-1 rounded-md bg-cyan-500/10 border border-cyan-400/20 font-mono text-cyan-200">
                CGPA {education.cgpa}
              </span>
              <span className="font-mono text-zinc-500">Class of {education.year}</span>
            </div>
          </motion.div>

          {/* Certifications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="p-6 rounded-2xl glass lg:col-span-1"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-400/30 flex items-center justify-center">
                <Award className="w-5 h-5 text-amber-300" />
              </div>
              <h3 className="font-display text-lg font-semibold text-white">Certifications</h3>
            </div>
            <ul className="space-y-3">
              {certifications.map((c) => (
                <li key={c.name} className="flex items-start justify-between gap-3 pb-3 border-b border-white/5 last:border-0 last:pb-0">
                  <div>
                    <p className="text-sm text-white">{c.name}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{c.issuer}</p>
                  </div>
                  <span className="font-mono text-xs text-amber-200">{c.year}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Download */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative p-6 rounded-2xl glass-strong overflow-hidden lg:col-span-1"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-amber-500/5 pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-300" />
                </div>
                <h3 className="font-display text-lg font-semibold text-white">Resume PDF</h3>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                One-page snapshot — projects, skills, internships, and contact details. Tailored for recruiters.
              </p>
              <a
                href={personalInfo.resumeUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex items-center gap-2 px-5 h-11 rounded-xl bg-cyan-400 hover:bg-cyan-300 text-zinc-950 font-semibold transition-colors"
              >
                <Download className="w-4 h-4" />
                Download Resume
                <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ResumeSection;
